import { FormMode, MasterDto, FormOptionDto} from "src/models";
import { CashFlowModels} from "../../models/cash-flows";

import { Dialog, DialogHeader, DialogContent, DialogTitle, DialogClose } from "src/components/ui/dialog";
import useApi from "src/configs/api";
import { useAlert } from "src/hooks/use-alert";
import { ReactEventHandler, ReactHTMLElement, use, useEffect, useState } from "react";
import SummerYouRadioGroup from "src/components/ui/input/radio-group";
import { ca, da } from "date-fns/locale";
import CustomSelect from "src/components/forms/custom-select";
import { toOptions } from "src/helpers/global";
import CustomInput from "src/components/forms/custom-input";
import CustomNumberInput from "src/components/forms/custom-number-input";
import { Button } from "src/components/ui/button";
import DatePicker from "src/components/date-picker";

function classNames(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

const FormDefault : Partial<CashFlowModels.Form> = {
    amount : null,
    categoryId : null,
    transactionDate : null,
    id : null,
    currencyCode : null    
}

export interface CashFlowFormProps {
    open : boolean,
    mode : FormMode;
    onOpenChange : (open : boolean) => void;
    // data? : CashFlowModels.Form | null
    data? : CashFlowModels.Form | null
}

const FormTitle: Record<FormMode, string> = {
  add: "New Transaction",
  edit: "Edit Transaction",
  view: "Transaction Details"
}

const DefaultValidation : Partial<CashFlowModels.FormErrors> = {
    amount : "",
    categoryId : "",
    transactionDate : "",
    currencyCode : ""  
}

export const CashFlowFormDialog = ( {open, mode, data, onOpenChange} : CashFlowFormProps) => {
    const callApi = useApi();
    const { showWarning, showSuccess, showError} = useAlert()

    const [transactionCategories, setTransactionCategories] = useState<FormOptionDto[]>([])
    const [currencyOptions, setCurrencyOptions] = useState<FormOptionDto[]>([])
    const [bankAccountOptions, setBankAccountOptions] = useState<FormOptionDto[]>([])
    const [formData, setFormData] = useState<Partial<CashFlowModels.Form>>(FormDefault);

    const [formValidation, setFormValidation] = useState<CashFlowModels.FormErrors>(DefaultValidation);

    useEffect(() => {
        FetchFormReferences();
    }, [])

    useEffect(() => {

        if (mode === "add") {
            setFormData(FormDefault);
        } else if (data) {
            setFormData({ ...FormDefault, ...data });
        }

        setFormValidation(DefaultValidation)

    }, [open, mode]);

    const FetchFormReferences = async () => {
        try {

            /* Categories */
            const res = await callApi.get("/master/transaction-categories");
            if (res.status === 200) 
            {                
                const categoryOptions = toOptions(res.data.data, "categoryId", "category");
                setTransactionCategories(categoryOptions);
            }

            /* Currencies */
            const resCurrencies = await callApi.get("master-data/currency");
            if(resCurrencies.status === 200)
            {
                const currencies = resCurrencies.data.data;
                let currencyOpts : FormOptionDto[] = currencies.map((item: any) => ({
                    label : `${item.currencyCode}`,
                    value : item.currencyCode
                } )  )

                setCurrencyOptions(currencyOpts);                
            }

            /* Account Sources */
            const resAccounts = await callApi.get("finance/bank-accounts");
            if (resAccounts.status === 200)
            {
                const accounts = resAccounts.data.data;
                let accountOpts : FormOptionDto[]= accounts.map((item: any) => ({
                    label : `${item.accountName} (${item.accountType})${item.accountNumber ? ` - ${item.accountNumber}` : ""}`,
                    value : item.accountId
                } )  )

                setBankAccountOptions(accountOpts);                 }
        }
        catch {}
    }

    const handleChanges = (field:string, value:any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        setFormValidation(prev => ({
            ...prev,
            [field] : ""
        }))
    }

    const handleNumericFieldChanges = (field: string, val: { value: number | null; display: string }) =>
    {
        setFormData(prev => ({
            ...prev,
            [field]: val.value
        }));

        setFormValidation(prev => ({
            ...prev,
            [field] : ""
        }))
    }

    const validateForm = () => {
        if (mode === "view") return true; // skip validation in view mode

        let isValid = true;
        const errors : CashFlowModels.FormErrors = {};

        if(formData.transactionDate === null || !formData.transactionDate) {
            errors.transactionDate = "Date is required"
            isValid = false;
        }

        if(formData.categoryId === null) {
            errors.categoryId = "Category is required"
            isValid = false;
        }

        if(formData.currencyCode === null) {
            errors.currencyCode = "Currency is required"
            isValid = false;
        }

        if (formData.amount === null || formData.amount === 0)
        {
            errors.amount = "Amount is required"
            isValid = false;
        }

        setFormValidation(errors);
        return isValid;
    }

    const handleSubmit = async (e : React.FormEvent<HTMLFormElement>) => {

        e.preventDefault();
        if (!validateForm()) {return;}

        try
        {
            if (mode === "add")
            {
                await callApi.post(`/finance/cash-flows`, formData);
                showSuccess("Created");
            }
        }
        catch
        {
            showError("", "An error occured while performing this request. Try again later");
        }
    }

    return(
            <DialogContent showCloseButton={false} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} >
                <DialogHeader className=" border-b border-solid">
                    <DialogTitle className="text-center m-2 ">
                        {FormTitle[mode]}
                    </DialogTitle>
                </DialogHeader>
                <form className="space-y-3" method="post" onSubmit={handleSubmit}>
                    <div>
                        <DatePicker 
                            label="Transaction Date"    
                            required
                            value={formData.transactionDate || undefined}
                            name="transactionDate" 
                            onChange={(val) => handleChanges("transactionDate", val)} 
                            errorMessage={formValidation.transactionDate}
                            formatStr="MMM dd yyyy"
                            showClearButton={true}
                        />
                    </div>

                    <div>
                       <CustomSelect 
                        options={transactionCategories} 
                        label="Category"
                        name="categoryId"
                        value={formData.categoryId}
                        errorMessage={formValidation.categoryId}
                        placeholder="Select category"
                        onChange={(val) => handleChanges("categoryId", val)}
                        required />
                    </div>

                    <div className="flex-1">
                        <div>
                            <label
                            htmlFor="amount" className="block text-sm font-semibold leading-6 text-gray-900">
                            Amount <span className="text-red-500 font-bolder ml-1">*</span>
                            </label> 
                        </div>
                        <div className="flex md:flex-3 flex-col md:flex-row md:space-x-2">
                            <div className="flex-2">
                                <CustomSelect 
                                options={currencyOptions} 
                                name="currencyCode"
                                value={formData.currencyCode}
                                errorMessage={formValidation.currencyCode}
                                placeholder="Select currency"
                                onChange={(val) => handleChanges("currencyCode", val)}
                                required />
                            </div>
                            <div className="flex-1">
                                <CustomNumberInput 
                                    // label="Category"
                                    name="amount"
                                    value={formData.amount}
                                    errorMessage={formValidation.amount}
                                    onChange={(val) => handleNumericFieldChanges("amount", val)}
                                    decimals={2}
                                    required />
                            </div>
                        </div>
                    </div>

                    <div>
                        <CustomSelect 
                        options={bankAccountOptions} 
                        label="Source of Account"
                        name="bankAccountId"
                        value={formData.accountId}
                        placeholder="Select Source (optional)"
                        onChange={(val) => handleChanges("accountId", val)} />                    
                    </div>

                    <div>
                        <CustomInput 
                            format="textarea"
                            label="Notes"
                            name="description"
                            value={formData.description}
                            onChange={(val) => handleChanges("description", val)}
                        />
                    </div>

                    <div className="flex flex-col pt-4 md:border-t-2 md:border-dashed md:flex-row-reverse gap-4">
                    {mode !== "view" && (
                        <Button type='submit' variant={"default"}  className="md:basis-2/3 bg-black text-white px-4 py-2 font-semibold md:py-1 rounded-lg">
                            {mode === "add" && "Save Account"}
                            {mode === "edit" && "Update Account"}
                        </Button>
                    )}
                    <DialogClose asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        className="px-3 py-1 text-red-500 border border-red-500 rounded-lg font-semibold hover:border-transparent hover:bg-red-300 hover:text-white"
                    >
                        Cancel
                    </Button>
                    </DialogClose>
                </div>

                </form>
            </DialogContent>
    )
}